import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Loader2 } from "lucide-react";

/**
 * Frontend of the Emergent-managed Google Auth flow.
 *
 * The user lands here at {origin}/auth/callback#session_id=<...>. We read
 * the session_id, POST it to /api/auth/google/callback, receive our own JWT
 * plus user profile, persist them, and navigate on.
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const { persistSession } = useAuth();
    const hasProcessed = useRef(false);
    const [msg, setMsg] = useState("Verifying your Google sign-in…");

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const query = window.location.search || "";
        const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
        const queryParams = new URLSearchParams(query);
        const session_id =
            hashParams.get("session_id") || queryParams.get("session_id");

        if (!session_id) {
            toast.error("No session id in URL — please try signing in again.");
            navigate("/login", { replace: true });
            return;
        }

        (async () => {
            try {
                const res = await api.post("/auth/google/callback", { session_id });
                // Clean the URL so we don't leak session_id.
                window.history.replaceState(null, "", "/auth/callback");
                persistSession(res.data);
                const u = res.data.user;
                // Route by shop owner status
                if (u.role === "SHOP_OWNER") {
                    const profileComplete =
                        !!u.business_name &&
                        !!u.business_category &&
                        !!u.business_district &&
                        !!u.business_type &&
                        !!u.mobile_number;
                    if (!profileComplete) {
                        navigate("/onboarding/shop", { replace: true });
                        return;
                    }
                    const docs = u.verification_docs || {};
                    const hasDocs = docs.shop_business_license && docs.pan_or_aadhaar;
                    if (!hasDocs) {
                        navigate("/merchant/onboarding", { replace: true });
                        return;
                    }
                    if (u.status === "PENDING_APPROVAL") {
                        navigate("/pending", { replace: true });
                        return;
                    }
                    if (u.status === "REJECTED") {
                        navigate("/rejected", { replace: true });
                        return;
                    }
                    navigate("/merchant/home", { replace: true });
                    return;
                }
                navigate("/dashboard", { replace: true });
            } catch (e) {
                toast.error(errorMessage(e));
                setMsg("Sign-in failed. Redirecting back to login…");
                setTimeout(() => navigate("/login", { replace: true }), 1500);
            }
        })();
    }, [navigate, persistSession]);

    return (
        <div className="flex min-h-screen w-full items-center justify-center bg-neutral-950 text-white">
            <div className="flex flex-col items-center gap-6 text-center">
                <Logo dark size={44} />
                <Loader2 className="animate-spin text-orange-500" size={28} />
                <div className="max-w-sm text-sm text-neutral-300">{msg}</div>
            </div>
        </div>
    );
}
